import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

// Cache modułowy — żeby nie odpytywać DB za każdym razem przy ponownym renderze
// formularza. Klucz = IATA miasta wylotu, wartość = uporządkowana lista IATA destynacji.
const cache = new Map<string, string[]>();
const inflight = new Map<string, Promise<string[]>>();

async function fetchTop(cityCode: string): Promise<string[]> {
  if (cache.has(cityCode)) return cache.get(cityCode)!;
  const existing = inflight.get(cityCode);
  if (existing) return existing;
  const p = (async () => {
    try {
      const { data } = await supabase
        .from("city_top_destinations")
        .select("dest_iata,rank")
        .eq("city_iata", cityCode)
        .order("rank", { ascending: true });
      const codes = (data ?? []).map((r) => r.dest_iata as string);
      cache.set(cityCode, codes);
      return codes;
    } catch {
      cache.set(cityCode, []);
      return [];
    } finally {
      inflight.delete(cityCode);
    }
  })();
  inflight.set(cityCode, p);
  return p;
}

/**
 * Zwraca top destynacje dla podanego miasta wylotu z `city_top_destinations`
 * (świeży ranking generowany cron-em z Firecrawl + AI). Pusta tablica gdy
 * brak danych — caller powinien wtedy spaść na hardcoded fallback.
 */
export function useTopDestinations(cityCode: string | undefined): string[] {
  const code = cityCode?.toUpperCase() ?? "";
  const [codes, setCodes] = useState<string[]>(() => (code ? cache.get(code) ?? [] : []));
  useEffect(() => {
    if (!code) {
      setCodes([]);
      return;
    }
    // Reset natychmiast przy zmianie miasta — żeby nie pokazywać starych
    // propozycji (np. ze SZZ) zanim dojedzie świeży fetch dla KTW.
    setCodes(cache.get(code) ?? []);
    let cancelled = false;
    fetchTop(code).then((c) => {
      if (!cancelled) setCodes(c);
    });
    return () => {
      cancelled = true;
    };
  }, [code]);
  return codes;
}
